import React, { useMemo, useState } from 'react';
import {
  X, Lock, ShieldCheck, BarChart3, ListChecks, KeyRound, Download, Plus, Trash2, Save,
  RotateCcw, MessageSquare, ChevronUp, ChevronDown, Eye,
} from 'lucide-react';
import SurveyModal from './SurveyModal';
import { useSurveyAdmin, fetchAdminHash } from '../hooks/useSurvey';
import {
  DEFAULT_QUESTIONS, QUESTION_TYPES, SCALE_MIN, SCALE_MAX, slugifyId,
} from '../data/surveyQuestions';
import { verifyPassword } from '../utils/adminAuth';
import { toast } from '../utils/toast';

// Ordinal ramp for the 1–5 distribution: ONE hue, light→dark, no step lighter
// than blue-250 so the lightest segment still reads against the surface.
// Validated (light mode, --ordinal): monotone L, ΔL ≥ 0.06, 2.06:1 at the light
// end. Do not swap these for a categorical set, the levels are ordered.
const SCALE_RAMP = ['#86b6ef', '#5598e7', '#2a78d6', '#1c5cab', '#104281'];
const CHOICE_COLOR = '#2a78d6';

const inputCls = 'w-full px-3 py-2 border border-line rounded-ctl text-body outline-none focus:ring-2 focus:border-accent focus:ring-accent/25';
const fmtDate = (ts) => (ts ? new Date(ts).toLocaleDateString([], { month: 'short', day: 'numeric' }) : ', ');

function StatTile({ label, value, hint }) {
  return (
    <div className="bg-surface border border-line rounded-card px-4 py-3">
      <p className="text-title font-semibold text-ink tabular-nums">{value}</p>
      <p className="text-meta text-ink-2 mt-0.5">{label}</p>
      {hint && <p className="text-meta text-ink-2 mt-0.5">{hint}</p>}
    </div>
  );
}

/**
 * Distribution of an ordered 1–5 answer as one segmented bar.
 * Every segment carries its own count as a direct label, so identity is never
 * colour-alone, and the counts row underneath doubles as the table view.
 */
function ScaleDistribution({ counts, total, low, high }) {
  return (
    <div>
      <div className="flex gap-[2px] h-7 rounded-ctl overflow-hidden bg-page" role="img"
        aria-label={`Distribution: ${counts.map((c, i) => `${i + SCALE_MIN}: ${c}`).join(', ')}`}>
        {counts.map((count, i) => (
          count > 0 ? (
            <div
              key={i}
              className="flex items-center justify-center transition-all"
              style={{ width: `${(count / total) * 100}%`, backgroundColor: SCALE_RAMP[i] }}
              title={`${i + SCALE_MIN}, ${count} response${count === 1 ? '' : 's'} (${Math.round((count / total) * 100)}%)`}
            >
              {/* The segment is labelled with its LEVEL, its identity. The
                  count is the width, and is spelled out in the row below and
                  on hover; putting the count in here reads as a level. */}
              <span className={`text-meta font-semibold ${i >= 2 ? 'text-white' : 'text-blue-950'}`}>
                {(count / total) > 0.06 ? i + SCALE_MIN : ''}
              </span>
            </div>
          ) : null
        ))}
      </div>
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-meta text-ink-2">{low || `${SCALE_MIN}, low`}</span>
        <div className="flex items-center gap-2.5">
          {counts.map((count, i) => (
            <span key={i} className="flex items-center gap-1 text-meta text-ink-2 tabular-nums">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: SCALE_RAMP[i] }} />
              {i + SCALE_MIN}: <span className="font-medium text-ink">{count}</span>
            </span>
          ))}
        </div>
        <span className="text-meta text-ink-2">{high || `${SCALE_MAX}, high`}</span>
      </div>
    </div>
  );
}

function ChoiceDistribution({ tally, total }) {
  return (
    <div className="space-y-1.5">
      {tally.map(([option, count]) => (
        <div key={option} className="flex items-center gap-2">
          <span className="text-meta text-ink w-40 flex-shrink-0 truncate" title={option}>{option}</span>
          <div className="flex-1 h-5 bg-page rounded-ctl overflow-hidden">
            <div
              className="h-full rounded-r-md"
              style={{ width: `${Math.max((count / total) * 100, 1)}%`, backgroundColor: CHOICE_COLOR }}
              title={`${option}, ${count} of ${total}`}
            />
          </div>
          <span className="text-meta text-ink-2 tabular-nums w-14 text-right">
            {count} · {Math.round((count / total) * 100)}%
          </span>
        </div>
      ))}
    </div>
  );
}

// ---- Password gate ----------------------------------------------------
function LockScreen({ onUnlock, onClose }) {
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  const tryUnlock = async () => {
    setChecking(true);
    setError('');
    const stored = await fetchAdminHash();
    const ok = await verifyPassword(value, stored);
    setChecking(false);
    if (ok) onUnlock();
    else setError('That password is not right.');
  };

  return (
    <div className="fixed inset-0 z-50 bg-page flex items-center justify-center p-4">
      <div className="bg-surface border border-line rounded-card shadow-pop w-full max-w-sm p-6">
        <div className="w-11 h-11 rounded-full bg-accent-tint flex items-center justify-center mb-3">
          <Lock className="w-5 h-5 text-accent" />
        </div>
        <h1 className="text-heading font-medium text-ink">Admin dashboard</h1>
        <p className="text-meta text-ink-2 mt-1 mb-4">
          Survey results and question setup. Facilitator access only.
        </p>
        <input
          type="password"
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && tryUnlock()}
          placeholder="Password"
          aria-label="Admin password"
          className={inputCls}
        />
        {error && <p className="text-meta text-flag-red mt-2">{error}</p>}
        <button
          onClick={tryUnlock}
          disabled={!value || checking}
          className="w-full mt-3 bg-accent text-white py-2.5 rounded-ctl text-body font-medium hover:bg-accent-strong transition disabled:opacity-50"
        >
          {checking ? 'Checking…' : 'Unlock'}
        </button>
        <button onClick={onClose} className="w-full mt-2 py-2 text-meta text-ink-2 hover:text-ink transition">
          Back to CDM Grid
        </button>
      </div>
    </div>
  );
}

// ---- Dashboard --------------------------------------------------------
export default function AdminDashboard({ onClose, onLock }) {
  const [tab, setTab] = useState('responses');
  const [caseFilter, setCaseFilter] = useState('all');
  const { questions, responses, loading, saveQuestions, setPassword, deleteResponse } = useSurveyAdmin(true);

  const cases = useMemo(() => {
    const seen = new Map();
    responses.forEach((r) => { if (r.caseTitle) seen.set(r.caseTitle, (seen.get(r.caseTitle) || 0) + 1); });
    return [...seen.entries()].sort((a, b) => b[1] - a[1]);
  }, [responses]);

  const rows = useMemo(
    () => (caseFilter === 'all' ? responses : responses.filter((r) => r.caseTitle === caseFilter)),
    [responses, caseFilter]
  );

  const stats = useMemo(() => ({
    responses: rows.length,
    people: new Set(rows.map((r) => r.identityKey)).size,
    runs: new Set(rows.map((r) => `${r.sessionId}/${r.runId}`)).size,
    sessions: new Set(rows.map((r) => r.sessionId)).size,
  }), [rows]);

  // Per question: the numbers for scales/choices, the raw answers for text.
  const analysis = useMemo(() => questions.map((q) => {
    const values = rows.map((r) => r.answers?.[q.id]).filter((v) => v !== undefined && v !== null && v !== '');
    if (q.type === 'scale') {
      const nums = values.map(Number).filter((n) => Number.isFinite(n));
      const counts = Array.from({ length: SCALE_MAX - SCALE_MIN + 1 }, (_, i) =>
        nums.filter((n) => Math.round(n) === i + SCALE_MIN).length);
      const mean = nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : null;
      return { q, kind: 'scale', counts, total: nums.length, mean };
    }
    if (q.type === 'choice') {
      const tally = new Map();
      values.forEach((v) => tally.set(String(v), (tally.get(String(v)) || 0) + 1));
      (q.options || []).forEach((o) => { if (!tally.has(o)) tally.set(o, 0); });
      return { q, kind: 'choice', tally: [...tally.entries()].sort((a, b) => b[1] - a[1]), total: values.length };
    }
    return { q, kind: 'text', total: values.length };
  }), [questions, rows]);

  const exportCsv = () => {
    const cell = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ['Date', 'Session', 'Case', 'Variant', 'Name', 'Role', ...questions.map((q) => q.prompt)];
    const body = rows.map((r) => [
      new Date(r.at).toISOString(), r.sessionId, r.caseTitle, r.streamLabel, r.name, r.role,
      ...questions.map((q) => r.answers?.[q.id] ?? ''),
    ]);
    const csv = [header, ...body].map((line) => line.map(cell).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `cdm-grid-survey-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const TABS = [
    ['responses', 'Results', BarChart3],
    ['questions', 'Questions', ListChecks],
    ['access', 'Access', KeyRound],
  ];

  return (
    <div className="fixed inset-0 z-50 bg-page flex flex-col">
      <header className="flex items-center justify-between px-4 sm:px-6 py-3 bg-surface border-b border-line flex-shrink-0">
        <h1 className="text-heading font-medium text-ink flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-accent" />Admin dashboard
        </h1>
        <div className="flex items-center gap-1">
          <button onClick={onLock} className="flex items-center gap-1.5 px-3 py-1.5 rounded-ctl text-meta text-ink-2 hover:bg-accent-tint transition">
            <Lock className="w-3.5 h-3.5" />Lock
          </button>
          <button onClick={onClose} className="p-2 rounded-ctl hover:bg-accent-tint text-ink-2" aria-label="Close dialog">
            <X className="w-5 h-5" />
          </button>
        </div>
      </header>

      <nav className="flex gap-1 px-4 sm:px-6 py-2 bg-surface border-b border-line flex-shrink-0">
        {TABS.map(([id, label, Icon]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-ctl text-meta font-medium transition ${
              tab === id ? 'bg-accent-tint text-accent' : 'text-ink-2 hover:bg-accent-tint'
            }`}
          >
            <Icon className="w-3.5 h-3.5" />{label}
          </button>
        ))}
      </nav>

      <main className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="max-w-5xl mx-auto space-y-4 pb-16">
          {tab === 'responses' && (
            <ResultsTab
              loading={loading}
              rows={rows}
              stats={stats}
              cases={cases}
              caseFilter={caseFilter}
              setCaseFilter={setCaseFilter}
              analysis={analysis}
              onExport={exportCsv}
              onDelete={deleteResponse}
            />
          )}
          {tab === 'questions' && <QuestionsTab questions={questions} onSave={saveQuestions} />}
          {tab === 'access' && <AccessTab onSetPassword={setPassword} />}
        </div>
      </main>
    </div>
  );
}

function ResultsTab({ loading, rows, stats, cases, caseFilter, setCaseFilter, analysis, onExport, onDelete }) {
  if (loading) return <p className="text-body text-ink-2">Loading responses…</p>;
  if (!rows.length && caseFilter === 'all') {
    return (
      <div className="bg-surface border border-line rounded-card p-8 text-center">
        <MessageSquare className="w-8 h-8 mx-auto text-ink-2/50 mb-2" />
        <p className="text-body text-ink-2">No feedback yet.</p>
        <p className="text-meta text-ink-2 mt-1">
          The survey pops up 30 seconds after the final diagnosis is revealed in a case.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Filters in one row, above the charts. */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={caseFilter}
          onChange={(e) => setCaseFilter(e.target.value)}
          className="px-3 py-1.5 border border-line rounded-ctl text-meta bg-surface outline-none focus:ring-2 focus:border-accent focus:ring-accent/25"
          aria-label="Filter by case"
        >
          <option value="all">All cases</option>
          {cases.map(([title, n]) => <option key={title} value={title}>{title} ({n})</option>)}
        </select>
        <button onClick={onExport} className="flex items-center gap-1.5 px-3 py-1.5 rounded-ctl text-meta font-medium text-ink-2 border border-line bg-surface hover:bg-accent-tint transition">
          <Download className="w-3.5 h-3.5" />Export CSV
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <StatTile label="Responses" value={stats.responses} />
        <StatTile label="People" value={stats.people} hint="unique devices/accounts" />
        <StatTile label="Case runs" value={stats.runs} />
        <StatTile label="Sessions" value={stats.sessions} />
      </div>

      {analysis.map(({ q, kind, counts, tally, total, mean }) => (
        <section key={q.id} className="bg-surface border border-line rounded-card p-4">
          <div className="flex items-start justify-between gap-3 mb-3">
            <h3 className="text-body font-medium text-ink">{q.prompt}</h3>
            <span className="text-meta text-ink-2 whitespace-nowrap tabular-nums">
              {mean != null && <span className="font-semibold text-ink">mean {mean.toFixed(1)} · </span>}
              n={total}
            </span>
          </div>
          {total === 0 && <p className="text-meta text-ink-2">No answers yet.</p>}
          {total > 0 && kind === 'scale' && (
            <ScaleDistribution counts={counts} total={total} low={q.low} high={q.high} />
          )}
          {total > 0 && kind === 'choice' && <ChoiceDistribution tally={tally} total={total} />}
          {kind === 'text' && total > 0 && (
            <ul className="space-y-1.5 max-h-72 overflow-y-auto">
              {rows.filter((r) => (r.answers?.[q.id] || '').toString().trim()).map((r) => (
                <li key={r.key} className="text-meta text-ink bg-page border border-line rounded-ctl px-3 py-2">
                  “{r.answers[q.id]}”
                  <span className="block text-meta text-ink-2 mt-0.5">
                    {r.name}{r.role === 'facilitator' ? ' (facilitator)' : ''} · {r.caseTitle}
                    {r.streamLabel ? ` · ${r.streamLabel}` : ''} · {fmtDate(r.at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}

      <section className="bg-surface border border-line rounded-card overflow-hidden">
        <h3 className="text-meta font-semibold text-ink uppercase tracking-wide px-4 py-2.5 bg-page border-b border-line">
          Every response ({rows.length})
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-meta">
            <thead className="text-ink-2">
              <tr className="border-b border-line">
                <th className="text-left font-medium px-4 py-2">Date</th>
                <th className="text-left font-medium px-4 py-2">Who</th>
                <th className="text-left font-medium px-4 py-2">Case</th>
                <th className="text-left font-medium px-4 py-2">Session</th>
                <th className="px-2" />
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 200).map((r) => (
                <tr key={r.key} className="border-b border-line last:border-0">
                  <td className="px-4 py-2 text-ink-2 whitespace-nowrap">{fmtDate(r.at)}</td>
                  <td className="px-4 py-2 text-ink">
                    {r.name}
                    {r.role === 'facilitator' && <span className="ml-1 text-meta text-flag-amber">facilitator</span>}
                  </td>
                  <td className="px-4 py-2 text-ink-2">
                    {r.caseTitle}{r.streamLabel ? ` · ${r.streamLabel}` : ''}
                  </td>
                  <td className="px-4 py-2 text-ink-2 font-mono text-meta">{r.sessionId}</td>
                  <td className="px-2 py-2">
                    <button onClick={() => onDelete(r)} className="text-ink-2/50 hover:text-flag-red" aria-label={`Delete response from ${r.name}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function QuestionsTab({ questions, onSave }) {
  const [draft, setDraft] = useState(() => questions.map((q) => ({ ...q })));
  const [dirty, setDirty] = useState(false);
  const [preview, setPreview] = useState(false);

  const patch = (i, changes) => {
    setDraft((d) => d.map((q, j) => (j === i ? { ...q, ...changes } : q)));
    setDirty(true);
  };
  const move = (i, delta) => {
    setDraft((d) => {
      const next = [...d];
      const j = i + delta;
      if (j < 0 || j >= next.length) return d;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    setDirty(true);
  };
  const add = () => {
    setDraft((d) => [...d, {
      id: slugifyId('new question', d.map((q) => q.id)),
      type: 'scale', prompt: '', low: '', high: '', options: [],
    }]);
    setDirty(true);
  };
  const remove = (i) => { setDraft((d) => d.filter((_, j) => j !== i)); setDirty(true); };

  const valid = draft.length > 0 && draft.every((q) => (q.prompt || '').trim());

  return (
    <>
      <div className="bg-surface border border-line rounded-card p-4">
        <h2 className="text-body font-medium text-ink">Survey questions</h2>
        <p className="text-meta text-ink-2 mt-1">
          Shown 30 seconds after the final diagnosis is revealed. Changes apply to every future case
          immediately, past answers keep the question they were asked, so renaming a question keeps
          its history only if you leave its id alone.
        </p>
      </div>

      {draft.map((q, i) => (
        <div key={q.id} className="bg-surface border border-line rounded-card p-3.5 space-y-2">
          <div className="flex items-start gap-2">
            <span className="text-meta font-semibold text-accent mt-2.5 w-4">{i + 1}.</span>
            <input
              className={inputCls}
              value={q.prompt}
              onChange={(e) => patch(i, { prompt: e.target.value })}
              placeholder="Question shown to students *"
            />
            <select
              className="px-2 py-2 border border-line rounded-ctl text-meta bg-surface outline-none"
              value={q.type}
              onChange={(e) => patch(i, { type: e.target.value })}
              aria-label="Question type"
            >
              {QUESTION_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <div className="flex flex-col">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="text-ink-2/50 hover:text-accent disabled:opacity-30" aria-label="Move up">
                <ChevronUp className="w-4 h-4" />
              </button>
              <button onClick={() => move(i, 1)} disabled={i === draft.length - 1} className="text-ink-2/50 hover:text-accent disabled:opacity-30" aria-label="Move down">
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
            <button onClick={() => remove(i)} className="p-1.5 text-ink-2/50 hover:text-flag-red mt-1" aria-label={`Remove question ${i + 1}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          {q.type === 'scale' && (
            <div className="grid sm:grid-cols-2 gap-2 pl-6">
              <input className={inputCls} value={q.low || ''} onChange={(e) => patch(i, { low: e.target.value })} placeholder="Label for 1 (e.g. Mostly listened)" />
              <input className={inputCls} value={q.high || ''} onChange={(e) => patch(i, { high: e.target.value })} placeholder="Label for 5 (e.g. Contributed throughout)" />
            </div>
          )}
          {q.type === 'choice' && (
            <div className="pl-6">
              <textarea
                rows={3}
                className={`${inputCls} resize-y`}
                value={(q.options || []).join('\n')}
                onChange={(e) => patch(i, { options: e.target.value.split('\n').map((x) => x.trim()).filter(Boolean) })}
                placeholder="One option per line"
              />
            </div>
          )}
          <p className="pl-6 text-meta text-ink-2 font-mono">id: {q.id}</p>
        </div>
      ))}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button onClick={add} className="flex items-center gap-1.5 text-meta text-accent hover:text-accent font-medium">
            <Plus className="w-3.5 h-3.5" />Add question
          </button>
          <button
            onClick={() => { setDraft(DEFAULT_QUESTIONS.map((q) => ({ options: [], ...q }))); setDirty(true); }}
            className="flex items-center gap-1.5 text-meta text-ink-2 hover:text-ink"
          >
            <RotateCcw className="w-3.5 h-3.5" />Reset to the defaults
          </button>
          <button
            onClick={() => setPreview(true)}
            disabled={!valid}
            className="flex items-center gap-1.5 text-meta text-ink-2 hover:text-ink disabled:opacity-40"
          >
            <Eye className="w-3.5 h-3.5" />Preview as a student
          </button>
        </div>
        <button
          onClick={() => { onSave(draft); setDirty(false); }}
          disabled={!valid || !dirty}
          className="flex items-center gap-1.5 bg-accent text-white px-4 py-2 rounded-ctl text-meta font-medium hover:bg-accent-strong transition disabled:opacity-50"
        >
          <Save className="w-4 h-4" />{dirty ? 'Save questions' : 'Saved'}
        </button>
      </div>

      {preview && (
        <SurveyModal
          questions={draft}
          caseTitle="a preview, nothing is recorded"
          onSkip={() => setPreview(false)}
          onSubmit={() => { setPreview(false); toast('Preview only, nothing was recorded.', 'info'); }}
        />
      )}
    </>
  );
}

function AccessTab({ onSetPassword }) {
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const ok = next.length >= 6 && next === confirm;

  return (
    <div className="bg-surface border border-line rounded-card p-4 max-w-md">
      <h2 className="text-body font-medium text-ink">Dashboard password</h2>
      <p className="text-meta text-ink-2 mt-1 mb-3">
        Changing it here takes effect everywhere immediately, no redeploy. Minimum 6 characters.
      </p>
      <div className="space-y-2">
        <input type="password" className={inputCls} value={next} onChange={(e) => setNext(e.target.value)} placeholder="New password" aria-label="New password" />
        <input type="password" className={inputCls} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" aria-label="Confirm new password" />
      </div>
      <button
        onClick={() => { onSetPassword(next); setNext(''); setConfirm(''); }}
        disabled={!ok}
        className="mt-3 bg-accent text-white px-4 py-2 rounded-ctl text-meta font-medium hover:bg-accent-strong transition disabled:opacity-50"
      >
        Change password
      </button>
      <p className="text-meta text-flag-amber bg-flag-amber/5 border border-flag-amber/40 rounded-ctl px-3 py-2 mt-4">
        This gate keeps the dashboard out of students' hands during a session. It is not a security
        boundary while the database rules are open, don't collect anything sensitive in the survey.
      </p>
      <button
        onClick={() => toast('Tip: the dashboard also opens at /?admin=1', 'info')}
        className="text-meta text-ink-2 hover:text-ink mt-3"
      >
        How do I get back here?
      </button>
    </div>
  );
}

export { LockScreen };
